// Copyright (c) Docugami, Inc. All rights reserved.

import { ColDef, ColGroupDef, Column, ColumnMovedEvent } from "ag-grid-community";
import { IReportRow } from "./AgGridReportTable";
import { useCallback } from "react";
import { useSetEntriesOrderMutation, useAddEntriesToGroupMutation, useRemoveEntriesFromGroupMutation, createOperationId } from "store/reports/reportsSlice";
import { orderEntriesAfterColumnMove } from "./reportTableHelpers";
import { EntityState } from "@reduxjs/toolkit";
import { ReportDefinition, ReportEntry } from "networking/reportsApi";

export type ReportColumnDragTool = {
    handleMovement: (args: {
        event: ColumnMovedEvent<IReportRow>;
        reportId: string;
        report: ReportDefinition;
        columns: (ColDef<IReportRow> | ColGroupDef<IReportRow>)[];
        entries: EntityState<ReportEntry>;
        isLoading: boolean;
        isPublished: boolean;
    }) => void;
}

export function useReportColumnDragTool(): ReportColumnDragTool {
    const [setEntriesOrder] = useSetEntriesOrderMutation();
    const [addEntriesToGroup] = useAddEntriesToGroupMutation();
    const [removeEntriesFromGroup] = useRemoveEntriesFromGroupMutation();

    const handleMovement = useCallback(async (args: {
        event: ColumnMovedEvent<IReportRow>;
        reportId: string;
        report: ReportDefinition;
        columns: (ColDef<IReportRow> | ColGroupDef<IReportRow>)[];
        entries: EntityState<ReportEntry>;
        isLoading: boolean;
        isPublished: boolean;
    }) => {
        const { event, reportId, columns, report, entries, isLoading, isPublished } = args;
        const { column, columns: movedColumns, toIndex, finished } = event;

        let isMovingColumnGroup = columns && !column;

        let draggedColumns: Column<IReportRow>[] = [];

        if (column) {
            draggedColumns = [column];
        } else if (movedColumns) {
            draggedColumns = movedColumns;
        }

        const missingToIndex = toIndex === null || toIndex === undefined;
        if (isLoading || isPublished || missingToIndex || draggedColumns.length === 0 || !finished) {
            return;
        }

        const { reorderedEntries, didReorder, groupChange } = orderEntriesAfterColumnMove({
            draggedColumns,
            destinationIndex: toIndex,
            columns,
            entryIds: report.entries,
            entries: entries,
            isMovingColumnGroup,
        });

        if (!didReorder) {
            return;
        }

        if (finished) {
            if (groupChange) {
                if (groupChange.type === 'add') {
                    await addEntriesToGroup({
                        reportId: reportId,
                        groupId: groupChange.targetGroupId!,
                        entries: [groupChange.sourceEntryId],
                        orderedEntries: reorderedEntries,
                        operationId: createOperationId()
                    });
                } else if (groupChange.type === 'remove') {
                    await removeEntriesFromGroup({
                        reportId: reportId,
                        groupId: groupChange.targetGroupId!,
                        entries: [groupChange.sourceEntryId],
                        orderedEntries: reorderedEntries,
                        operationId: createOperationId()
                    });
                }
            }

            // Set the final order only when the drag is finished
            await setEntriesOrder({
                reportId,
                entryIds: reorderedEntries,
                operationId: createOperationId()
            });
        }
    }, [addEntriesToGroup, removeEntriesFromGroup, setEntriesOrder]);

    return {
        handleMovement,
    }
}
